<template>
  <div ref="container" class="webgl-orb-container"></div>
</template>

<script>
import * as THREE from "three";
import { markRaw } from "vue";
import vertexShader from "./shaders/vertexShader.glsl";
import fragmentShader from "./shaders/fragmentShader.glsl";

export default {
  name: "WebGLOrb",
  props: {
    size: {
      type: Number,
      default: 90,
    },
    isPulsing: {
      type: Boolean,
      default: false,
    },
    intensity: {
      type: Number,
      default: 1.0,
    },
    animationSpeed: {
      type: Number,
      default: 1.0,
    },
    colorPrimary: {
      type: String,
      default: "#6ee7f9", // Cyan
    },
    colorSecondary: {
      type: String,
      default: "#8f8ae6", // Purple
    },
    colorTertiary: {
      type: String,
      default: "#e5a3ff", // Pink
    },
    pulseSpeed: {
      type: Number,
      default: 2.0,
    },
    pulseAmount: {
      type: Number,
      default: 0.05,
    },
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      mesh: null,
      animationId: null,
      startTime: Date.now(),
    };
  },
  computed: {
    colorPrimaryVec3() {
      return this.hexToVec3(this.colorPrimary);
    },
    colorSecondaryVec3() {
      return this.hexToVec3(this.colorSecondary);
    },
    colorTertiaryVec3() {
      return this.hexToVec3(this.colorTertiary);
    },
  },
  mounted() {
    // bind animate once so requestAnimationFrame keeps correct `this`
    this.animate = this.animate.bind(this);
    this.initThree();
    this.animate();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    this.cleanup();
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    size() {
      this.handleResize();
    },
    intensity(newVal) {
      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.intensity.value = newVal;
      }
    },
    animationSpeed(newVal) {
      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.animationSpeed.value = newVal;
      }
    },
    colorPrimary() {
      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.colorPrimary.value = this.colorPrimaryVec3;
      }
    },
    colorSecondary() {
      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.colorSecondary.value =
          this.colorSecondaryVec3;
      }
    },
    colorTertiary() {
      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.colorTertiary.value =
          this.colorTertiaryVec3;
      }
    },
  },
  methods: {
    initThree() {
      // Create scene (mark raw to avoid Vue proxy wrapping Three internals)
      this.scene = markRaw(new THREE.Scene());

      // Create orthographic camera for pixel-perfect 2D rendering
      this.camera = markRaw(
        new THREE.OrthographicCamera(
          -1, // left
          1, // right
          1, // top
          -1, // bottom
          0.1,
          1000
        )
      );
      this.camera.position.z = 1;

      // Create renderer
      this.renderer = markRaw(
        new THREE.WebGLRenderer({
          alpha: true,
          antialias: true,
        })
      );
      this.renderer.setSize(this.size, this.size);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.$refs.container.appendChild(this.renderer.domElement);

      // Create plane geometry (like Shadertoy uses a 2D canvas)
      const geometry = markRaw(new THREE.PlaneGeometry(2, 2));

      // Create shader material
      const material = markRaw(
        new THREE.ShaderMaterial({
          vertexShader,
          fragmentShader,
          uniforms: {
            iTime: { value: 0 },
            iResolution: { value: new THREE.Vector2(this.size, this.size) },
            intensity: { value: this.intensity },
            animationSpeed: { value: this.animationSpeed },
            colorPrimary: { value: this.colorPrimaryVec3 },
            colorSecondary: { value: this.colorSecondaryVec3 },
            colorTertiary: { value: this.colorTertiaryVec3 },
          },
          transparent: true,
          side: THREE.DoubleSide,
        })
      );

      // Create mesh
      this.mesh = markRaw(new THREE.Mesh(geometry, material));
      this.scene.add(this.mesh);
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate);

      // Update time uniform
      const elapsed = (Date.now() - this.startTime) / 1000;
      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.iTime.value = elapsed;
      }

      // Add pulsing effect if enabled
      if (this.isPulsing && this.mesh) {
        const pulse =
          1.0 + Math.sin(elapsed * this.pulseSpeed) * this.pulseAmount;
        this.mesh.scale.set(pulse, pulse, 1);

        // Also scale the canvas so the shadow pulses with it
        if (this.renderer && this.renderer.domElement) {
          this.renderer.domElement.style.transform = `scale(${pulse})`;
        }
      } else if (this.renderer && this.renderer.domElement) {
        // Reset scale when not pulsing
        this.renderer.domElement.style.transform = "scale(1)";
      }

      // Render
      this.renderer.render(this.scene, this.camera);
    },

    hexToVec3(hex) {
      // Convert hex color to THREE.Vector3
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? new THREE.Vector3(
            parseInt(result[1], 16) / 255,
            parseInt(result[2], 16) / 255,
            parseInt(result[3], 16) / 255
          )
        : new THREE.Vector3(0.5, 0.5, 0.5);
    },

    handleResize() {
      if (!this.renderer || !this.camera) return;

      this.renderer.setSize(this.size, this.size);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      if (this.mesh && this.mesh.material.uniforms) {
        this.mesh.material.uniforms.iResolution.value.set(this.size, this.size);
      }
    },

    cleanup() {
      // Cancel animation
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }

      // Dispose of Three.js resources
      if (this.mesh) {
        this.mesh.geometry.dispose();
        this.mesh.material.dispose();
      }

      if (this.renderer) {
        this.renderer.dispose();
        if (this.$refs.container && this.renderer.domElement) {
          this.$refs.container.removeChild(this.renderer.domElement);
        }
      }

      // Clear references
      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.mesh = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.webgl-orb-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.webgl-orb-container >>> canvas {
  border-radius: 50%;
  transition: transform 0.1s ease-out;
}
</style>
